import { Injectable } from '@angular/core';
import { ControllerType, HttpClientService } from '../http-client.service';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { CreateCalibration } from 'app/dtos/calibration/createCalibration';
import { UpdateCalibration } from 'app/dtos/calibration/updateCalibration';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class CalibrationService {

  constructor(private httpClientService: HttpClientService) { }

  async getAllCalibrations(page, size, search?: string, startDate?: string, endDate?: string, companyId?: number, accreditationOgranization?: number, status?: number) {
    const queryParams = new URLSearchParams();

    queryParams.append("Page", page.toString());
    queryParams.append("Size", size.toString());

    if (startDate !== null && startDate !== undefined) queryParams.append("StartDate", startDate);
    if (endDate !== null && endDate !== undefined) queryParams.append("EndDate", endDate);
    if (companyId !== null && companyId !== undefined) queryParams.append("CompanyId", companyId.toString());
    if (status !== null && status !== undefined) queryParams.append("Status", status.toString());
    if (search !== null && search !== undefined) queryParams.append("search", search.toString());
    if (accreditationOgranization !== null && accreditationOgranization !== undefined) {
      queryParams.append("AccreditationOgranization", accreditationOgranization.toString());
    }
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration,
      queryString: queryParams.toString()
    });
    return await firstValueFrom(observable);
  }

  async getByIdCalibration(id) {
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration
    }, id);
    return await firstValueFrom(observable);
  }

  async getByIdReadyCalibration(id) {
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration,
      action: "GetByIdReadyCalibration"
    }, id);
    return await firstValueFrom(observable);
  }

  async getCalibrationMeasurementResultBase64(path: string) {
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration,
      action: "GetCalibrationMeasurementResultBase64"
    }, path);
    return await firstValueFrom(observable);
  }

  async createCalibration(createCalibration: CreateCalibration) {
    const formData = new FormData();

    formData.append("DeliveryDeviceId", createCalibration.deliveryDeviceId.toString());
    formData.append("ProcedureId", createCalibration.procedureId.toString());
    formData.append("PersonId", createCalibration.personId.toString());
    formData.append("CalibrationDate", createCalibration.calibrationDate.toISOString());
    formData.append("Status", createCalibration.status.toString());
    formData.append("EnvironmentalConditionId", createCalibration.environmentalConditionId.toString());
    formData.append("AccreditationOrganization", createCalibration.accreditationOrganization.toString());
    formData.append("IsPlace", createCalibration.isPlace ? "true" : "false");
    formData.append("SaveFilePath", createCalibration.saveFilePath);
    formData.append("TurkakCalibrationId", createCalibration.turkakCalibrationId ?? "");
    formData.append("PlaceAddress", createCalibration.placeAddress);

    if (createCalibration.measurementFile) {
      formData.append("MeasurementFile", createCalibration.measurementFile);
    }

    createCalibration.createCalibrationReferenceDevices.forEach((item, index) => {
      formData.append(`CreateCalibrationReferenceDevices[${index}].DeviceId`, item.deviceId.toString());
    });
    formData.append("visualAndFunctional", createCalibration.visualAndFunctional);

    const observable: Observable<any> = this.httpClientService.post({
      controller: ControllerType.Calibration,
    }, formData);
    return await firstValueFrom(observable);
  }

  async updateCalibration(updateCalibration: UpdateCalibration) {
    const formData = new FormData();

    formData.append('Id', updateCalibration.id.toString());
    formData.append('ProcedureId', updateCalibration.procedureId.toString());
    formData.append('PersonId', updateCalibration.personId.toString());
    formData.append('CalibrationDate', updateCalibration.calibrationDate.toISOString());
    formData.append('Status', updateCalibration.status.toString());
    formData.append('EnvironmentalConditionId', updateCalibration.environmentalConditionId.toString());
    formData.append('AccreditationOrganization', updateCalibration.accreditationOrganization.toString());
    formData.append('IsPlace',  updateCalibration.isPlace ? "true" : "false");
    formData.append('PlaceAddress', updateCalibration.placeAddress);
    formData.append('VisualAndFunctional', updateCalibration.visualAndFunctional.toString());
  
    if (updateCalibration.acceptanceDate) {
      formData.append('AcceptanceDate', updateCalibration.acceptanceDate.toISOString());
    }
  
    if (updateCalibration.turkakCalibrationId) {
      formData.append('TurkakCalibrationId', updateCalibration.turkakCalibrationId);
    }
  
    // 🔥 Dosya
    if (updateCalibration.measurementFile) {
      formData.append('MeasurementFile', updateCalibration.measurementFile);
    }
  
    // 🔥 Dosya adı gövdesi
    if (updateCalibration.saveFilePath) {
      formData.append('SaveFilePath', updateCalibration.saveFilePath);
    }
    
    const observable: Observable<any> = this.httpClientService.put({
      controller: ControllerType.Calibration
    }, formData);
    return await firstValueFrom(observable);
  }

  async updateCalibrationStatus(id, status) {
    const urlParameter = `${id}/${status}`;
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration,
      action: "UpdateCalibrationStatus",
      queryString: `/${status}`
    }, urlParameter);
    return await firstValueFrom(observable);
  }

  async getByIdCompanyCalibrations(id: number, search?: string, startDate?: string, endDate?: string) {
    let queryParams = `search=${search ?? ''}`;
    if (startDate) queryParams += `&startDate=${startDate}`;
    if (endDate) queryParams += `&endDate=${endDate}`;
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration,
      action: "GetCompanyByIdCalibrations",
      queryString: queryParams
    }, id);
    return await firstValueFrom(observable);
  }


  async getCompanyByIdCalibrations(page, size, id) {
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration,
      action: "GetCompanyByIdCalibrations",
      queryString: `Page=${page}&Size=${size}&id=${id}`
    });
    return await firstValueFrom(observable);
  }

  async deleteCalibration(id: number, token?: string) {
    console.log(token);
    const options: any = {
      controller: ControllerType.Calibration
    };

    // 🔥 Token varsa header'a ekle
    if (token) {
      options.headers = new HttpHeaders({
        "X-Token": token || ""
      });
    }

    const observable: Observable<any> = this.httpClientService.delete(options, id);
    return await firstValueFrom(observable);
  }

  async GetMeasurementExcel(path) {
    const observable: Observable<any> = this.httpClientService.get({
      controller: ControllerType.Calibration,
      action: "measurement-excel",
    }, path, 'blob');
    return await firstValueFrom(observable);
  }

  private eventSubject = new Subject<boolean>();

  event$ = this.eventSubject.asObservable();

  sendEvent(result: boolean) {
    this.eventSubject.next(result);
  }
}
